import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner, InputGroup, OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import { FaInfoCircle, FaChevronDown, FaChevronRight, FaUserShield } from "react-icons/fa";
import api from "../../api";
import { toast } from "react-toastify";
import "./AdminUnitPage.css";
import "./AccessManagementRolesPage.css";

// Canonical column order per the required matrix columns; any additional
// permission_key values that exist in the database but aren't part of this
// standard set are appended afterwards (alphabetically) rather than hidden —
// "load all active permissions automatically" must never silently drop a
// real permission just because it doesn't fit the 9 suggested columns.
const CANONICAL_ACTIONS = ["view", "create", "edit", "delete", "assign", "approve", "review", "export", "manage"];
const COLUMN_LABELS = {
  view: "View", create: "Create", edit: "Edit", delete: "Delete", assign: "Assign",
  approve: "Approve", review: "Review", export: "Export", manage: "Manage",
};
// Task §A: must come from the existing role_permissions schema/conventions.
const SUPPORTED_SCOPES = ["SELF", "TEAM", "DEPARTMENT", "UNIT", "ALL"];

// Presentation only: fixed column widths (px) shared by <colgroup> and the table's
// total width, so the header cells and every body row use identical widths.
const MATRIX_COL_WIDTH = { module: 220, route: 150, permission: 120 };

function labelFor(key) {
  return COLUMN_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function InfoTip({ text }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <span className="am-info" role="img" aria-label="info"><FaInfoCircle /></span>
    </OverlayTrigger>
  );
}

// Same deterministic role -> accent color + initials helpers as
// AccessManagementUsersPage.jsx (duplicated rather than shared, per that
// page's own precedent -- these two Access Management pages are already
// independent siblings, each with their own CSS file/helpers). Purely
// presentational; never used for any access-control decision.
const ROLE_COLORS = [
  { bg: "#f3e8ff", fg: "#7c3aed" },
  { bg: "#e0f2fe", fg: "#0369a1" },
  { bg: "#fef3c7", fg: "#92400e" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#fbeef1", fg: "#6d1530" },
  { bg: "#ffe4e6", fg: "#be123c" },
  { bg: "#fee2e2", fg: "#b91c1c" },
  { bg: "#ede9fe", fg: "#5b21b6" },
];
function colorForRole(roleName) {
  const key = String(roleName || "").trim().toLowerCase();
  if (!key) return ROLE_COLORS[4];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return ROLE_COLORS[hash % ROLE_COLORS.length];
}
function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

// Reads the effective (grant, scope) for a permission given a pending-change
// map snapshot -- a pure function so it can be used both from render and
// from inside a setState updater without relying on a stale closure.
function readState(map, permissionId, original) {
  if (map.has(permissionId)) return map.get(permissionId);
  return { granted: original.granted, dataScope: original.dataScope };
}

export default function AccessManagementRolesPage() {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [role, setRole] = useState(null);
  const [modules, setModules] = useState([]); // [{id, moduleKey, moduleName, permissions:[{id,key,name,granted,dataScope}]}]

  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | GRANTED | NOT_GRANTED
  const [expanded, setExpanded] = useState(() => new Set());

  // permissionId -> { granted, dataScope }
  const [pendingChanges, setPendingChanges] = useState(new Map());
  const [confirmAction, setConfirmAction] = useState(null); // { title, message, requiresScope, scope, onConfirm(scope) }

  useEffect(() => {
    setLoadingRoles(true);
    api.get("/admin/access-management/roles")
      .then((res) => setRoles(res.data?.data || []))
      .catch((err) => setError(err?.response?.data?.message || "Failed to load roles."))
      .finally(() => setLoadingRoles(false));
  }, []);

  const loadMatrix = (roleId) => {
    if (!roleId) return;
    setLoadingMatrix(true);
    setError(null);
    setPendingChanges(new Map());
    api.get(`/admin/access-management/roles/${roleId}/permissions`)
      .then((res) => {
        setRole(res.data?.data?.role || null);
        setModules(res.data?.data?.modules || []);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Failed to load this role's permissions.");
        setRole(null);
        setModules([]);
      })
      .finally(() => setLoadingMatrix(false));
  };

  useEffect(() => { if (selectedRoleId) loadMatrix(selectedRoleId); }, [selectedRoleId]);

  function selectRole(roleId) {
    setSelectedRoleId(String(roleId));
  }

  function handleRoleCardKeyDown(e, roleId) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      selectRole(roleId);
    }
  }

  // Every distinct permission_key across every module, canonical order first.
  const allColumns = useMemo(() => {
    const seen = new Set();
    modules.forEach((m) => m.permissions.forEach((p) => seen.add(p.key)));
    const extras = [...seen].filter((k) => !CANONICAL_ACTIONS.includes(k)).sort();
    return [...CANONICAL_ACTIONS.filter((k) => seen.has(k)), ...extras];
  }, [modules]);

  // Read-only display aggregate over the already-fetched permission matrix
  // -- purely a presentational summary (mirrors AccessManagementUsersPage.jsx's
  // accessSummary), never a second source of truth: it does not change what
  // is fetched, saved, or how a permission's granted state is computed.
  // Reflects pending (unsaved) toggles too, so the hero stays in sync with
  // the matrix as the admin edits it.
  const roleSummary = useMemo(() => {
    if (!role) return null;
    let total = 0, granted = 0;
    modules.forEach((m) => m.permissions.forEach((p) => {
      total += 1;
      if (readState(pendingChanges, p.id, p).granted) granted += 1;
    }));
    return { total, granted, notGranted: total - granted };
  }, [role, modules, pendingChanges]);

  function toggleGrant(permissionId, original) {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      const current = readState(prev, permissionId, original);
      const grantedNow = !current.granted;
      // Falls back to the ORIGINAL scope (not just the current pending one)
      // so toggling off then back on restores exactly the starting state,
      // rather than losing the scope and looking like a no-op diff with a
      // missing scope.
      const nextState = { granted: grantedNow, dataScope: grantedNow ? (current.dataScope || original.dataScope || null) : null };
      if (nextState.granted === original.granted && nextState.dataScope === original.dataScope) next.delete(permissionId);
      else next.set(permissionId, nextState);
      return next;
    });
  }

  function setScope(permissionId, original, scope) {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      const nextState = { granted: true, dataScope: scope || null };
      if (original.granted && original.dataScope === nextState.dataScope) next.delete(permissionId);
      else next.set(permissionId, nextState);
      return next;
    });
  }

  // Bulk grant only ever touches permissions NOT already granted (leaves any
  // already-granted permission's existing scope untouched) -- a single bulk
  // scope choice must never silently overwrite a deliberately-different
  // scope someone already set on another permission.
  function applyBulkGrant(items, scope) {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      items.forEach(({ id, original }) => {
        if (original.granted) return;
        next.set(id, { granted: true, dataScope: scope });
      });
      return next;
    });
  }
  function applyBulkClear(items) {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      items.forEach(({ id, original }) => {
        const nextState = { granted: false, dataScope: null };
        if (!original.granted) next.delete(id);
        else next.set(id, nextState);
      });
      return next;
    });
  }

  const filteredModules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return modules
      .map((m) => ({
        ...m,
        permissions: m.permissions.filter((p) => {
          if (statusFilter === "ALL") return true;
          const eff = readState(pendingChanges, p.id, p);
          return statusFilter === "GRANTED" ? eff.granted : !eff.granted;
        }),
      }))
      .filter((m) => {
        if (!q) return true;
        return m.moduleName.toLowerCase().includes(q) || m.moduleKey.toLowerCase().includes(q);
      })
      .filter((m) => statusFilter === "ALL" || m.permissions.length > 0);
  }, [modules, search, statusFilter, pendingChanges]);

  const hasPendingChanges = pendingChanges.size > 0;
  const pendingMissingScope = useMemo(
    () => [...pendingChanges.values()].some((v) => v.granted && !v.dataScope),
    [pendingChanges]
  );

  function toggleExpand(moduleId) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId); else next.add(moduleId);
      return next;
    });
  }

  function confirmBulk(title, message, onConfirm, requiresScope = false) {
    setConfirmAction({ title, message, onConfirm, requiresScope, scope: "" });
  }

  function selectAllForModule(m) {
    const items = m.permissions.map((p) => ({ id: p.id, original: p }));
    confirmBulk(
      `Grant all permissions for ${m.moduleName}?`,
      `Choose the data scope to apply to every not-yet-granted action in "${m.moduleName}". Already-granted permissions keep their current scope.`,
      (scope) => applyBulkGrant(items, scope),
      true
    );
  }
  function clearAllForModule(m) {
    const items = m.permissions.map((p) => ({ id: p.id, original: p }));
    confirmBulk(`Revoke all permissions for ${m.moduleName}?`, `This will deselect every action currently visible for "${m.moduleName}".`, () => applyBulkClear(items));
  }
  function selectAllViewPermissions() {
    const items = [];
    filteredModules.forEach((m) => m.permissions.forEach((p) => { if (p.key === "view") items.push({ id: p.id, original: p }); }));
    confirmBulk(
      "Grant View for every visible module?",
      "Choose the data scope to apply to every not-yet-granted View permission currently visible.",
      (scope) => applyBulkGrant(items, scope),
      true
    );
  }
  function selectAllPermissions() {
    const items = [];
    filteredModules.forEach((m) => m.permissions.forEach((p) => items.push({ id: p.id, original: p })));
    confirmBulk(
      "Grant every visible permission?",
      "Choose the data scope to apply to every not-yet-granted permission currently visible.",
      (scope) => applyBulkGrant(items, scope),
      true
    );
  }
  function clearAllPermissions() {
    const items = [];
    filteredModules.forEach((m) => m.permissions.forEach((p) => items.push({ id: p.id, original: p })));
    confirmBulk("Clear every visible permission?", "This will deselect every permission currently visible across all modules.", () => applyBulkClear(items));
  }

  async function handleSave() {
    if (!selectedRoleId || !hasPendingChanges) return;
    if (pendingMissingScope) {
      toast.error("Choose a data scope for every newly-granted permission before saving.");
      return;
    }
    setSaving(true);
    try {
      const changes = [...pendingChanges.entries()].map(([permissionId, v]) => ({ permissionId, granted: v.granted, dataScope: v.dataScope }));
      await api.put(`/admin/access-management/roles/${selectedRoleId}/permissions`, { changes });
      toast.success(`Saved ${changes.length} permission change(s).`);
      loadMatrix(selectedRoleId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save permission changes.");
    } finally {
      setSaving(false);
    }
  }

  function handleDiscard() {
    setPendingChanges(new Map());
  }

  const roleColor = colorForRole(role?.name);

  return (
    <div className="unit-page access-roles-theme">
      <div className="unit-header arp-heading-box">
        <div className="arp-heading-content">
          <div className="arp-heading-icon">
            <i className="fa-solid fa-user-shield" aria-hidden="true"></i>
          </div>
          <div>
            <div className="unit-page-label">Access Management</div>
            <h2>Role Permissions</h2>
            <p>Select a role to view and edit its module permissions.</p>
          </div>
        </div>
      </div>

      <div className="arp-layout">
        {/* ---------- Roles: left panel, card list ---------- */}
        <div className="unit-card arp-roles-section">
          <div className="arp-roles-panel-header">
            <h5>Roles</h5>
            <p>{roles.length} role{roles.length === 1 ? "" : "s"}</p>
          </div>

          {loadingRoles && <div className="am-empty"><Spinner animation="border" size="sm" /> Loading roles…</div>}
          {!loadingRoles && roles.length === 0 && !error && (
            <div className="am-empty">No roles found.</div>
          )}

          {/* Mobile only (see @media in the <style> block below): the same
              `selectRole` handler as the card list, just via a native
              <select> instead of clicking a card -- no new selection logic. */}
          {!loadingRoles && roles.length > 0 && (
            <Form.Select
              className="arp-role-mobile-select"
              aria-label="Select a role"
              value={selectedRoleId}
              onChange={(e) => selectRole(e.target.value)}
            >
              <option value="">Select a role…</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name} ({r.user_count ?? 0})</option>
              ))}
            </Form.Select>
          )}

          {!loadingRoles && roles.length > 0 && (
            <div className="arp-role-card-list" role="listbox" aria-label="Roles">
              {roles.map((r) => {
                const isSelected = String(r.id) === String(selectedRoleId);
                const c = colorForRole(r.name);
                return (
                  <div
                    key={r.id}
                    role="option"
                    aria-label={r.name}
                    className={`arp-role-card${isSelected ? " arp-role-card-selected" : ""}`}
                    style={isSelected ? { borderLeftColor: c.fg } : undefined}
                    onClick={() => selectRole(r.id)}
                    onKeyDown={(e) => handleRoleCardKeyDown(e, r.id)}
                    tabIndex={0}
                    aria-selected={isSelected}
                  >
                    <div className="arp-role-card-avatar" style={{ background: c.bg, color: c.fg }}>
                      {initialsFor(r.name)}
                    </div>
                    <div className="arp-role-card-text">
                      <strong className="arp-role-card-name">{r.name}</strong>
                      <span className="arp-role-card-count">{r.user_count ?? 0} user{Number(r.user_count) === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ---------- Role details + permission matrix: right panel ---------- */}
        <div className="unit-card arp-details-section">
          {!role && !loadingMatrix && !error && (
            <div className="arp-empty-state">
              <div className="arp-empty-icon"><FaUserShield /></div>
              <h5>Select a role</h5>
              <p>Choose a role from the list to view and edit its module permissions.</p>
            </div>
          )}

          {loadingMatrix && (
            <div className="am-empty"><Spinner animation="border" size="sm" /> Loading permissions…</div>
          )}

          {error && (
            <div className="am-empty am-error">
              {error}
              <Button className="unit-btn unit-btn-light" size="sm" onClick={() => loadMatrix(selectedRoleId)}>
                <i className="fa-solid fa-rotate-right" aria-hidden="true"></i> Try Again
              </Button>
            </div>
          )}

          {role && !loadingMatrix && !error && (
            <>
              <div className="arp-hero">
                <div className="arp-hero-avatar" style={{ background: roleColor.bg, color: roleColor.fg }}>
                  {initialsFor(role.name)}
                </div>
                <div className="arp-hero-text">
                  <h3>{role.name}</h3>
                  <p className="arp-hero-sub">Configure access levels for this role across all modules.</p>
                </div>
              </div>

              {roleSummary && (
                <div className="aup-access-summary">
                  <div className="aup-summary-chip">
                    <i className="fa-solid fa-key" aria-hidden="true"></i>
                    <strong>{roleSummary.total}</strong>
                    <span>Permission{roleSummary.total === 1 ? "" : "s"}</span>
                  </div>
                  <div className="aup-summary-chip aup-summary-chip-success">
                    <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                    <strong>{roleSummary.granted}</strong>
                    <span>Granted</span>
                  </div>
                  <div className="aup-summary-chip aup-summary-chip-danger">
                    <i className="fa-solid fa-circle-xmark" aria-hidden="true"></i>
                    <strong>{roleSummary.notGranted}</strong>
                    <span>Not Granted</span>
                  </div>
                </div>
              )}

              <div className="unit-toolbar arp-matrix-toolbar">
                <div className="am-filters-group">
                  <InputGroup className="unit-search am-search-module">
                    <InputGroup.Text aria-hidden="true"><i className="fa-solid fa-magnifying-glass"></i></InputGroup.Text>
                    <Form.Control placeholder="Search module…" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </InputGroup>

                  <Form.Select className="am-status-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="ALL">All permissions</option>
                    <option value="GRANTED">Granted only</option>
                    <option value="NOT_GRANTED">Not granted only</option>
                  </Form.Select>
                </div>

                <div className="unit-toolbar-actions">
                  <Button className="unit-btn unit-btn-light" onClick={selectAllViewPermissions}>
                    <i className="fa-solid fa-eye" aria-hidden="true"></i>
                    Select All View
                  </Button>
                  <Button className="unit-btn unit-btn-light" onClick={selectAllPermissions}>
                    <i className="fa-solid fa-check-double" aria-hidden="true"></i>
                    Select All
                  </Button>
                  <Button className="unit-btn unit-btn-light" onClick={clearAllPermissions}>
                    <i className="fa-solid fa-eraser" aria-hidden="true"></i>
                    Clear All
                  </Button>
                  <Button
                    className={`unit-btn unit-btn-primary${hasPendingChanges ? " am-save-btn-active" : ""}`}
                    onClick={handleSave}
                    disabled={!hasPendingChanges || saving}
                  >
                    {saving ? <><Spinner size="sm" animation="border" /> Saving…</> : <><i className="fa-solid fa-floppy-disk" aria-hidden="true"></i> Save Changes</>}
                  </Button>
                </div>
              </div>

              {hasPendingChanges && (
                <div className="am-unsaved-banner">
                  <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                  You have {pendingChanges.size} unsaved change(s).
                  {pendingMissingScope && <span className="am-scope-warning">Some newly-granted permissions still need a data scope.</span>}
                  <Button variant="link" size="sm" onClick={handleDiscard}>
                    <i className="fa-solid fa-rotate-left" aria-hidden="true"></i> Discard
                  </Button>
                </div>
              )}

              <div className="unit-table-wrapper am-matrix-wrapper">
                {filteredModules.length === 0 ? (
                  <div className="am-empty">No modules match your search/filter.</div>
                ) : (
                  <table
                    className="unit-table am-matrix"
                    style={{
                      width: MATRIX_COL_WIDTH.module + MATRIX_COL_WIDTH.route + allColumns.length * MATRIX_COL_WIDTH.permission,
                      minWidth: "100%",
                    }}
                  >
                    <colgroup>
                      <col className="am-col-module" style={{ width: MATRIX_COL_WIDTH.module }} />
                      <col className="am-col-route" style={{ width: MATRIX_COL_WIDTH.route }} />
                      {allColumns.map((col) => <col key={col} className="am-col-permission" style={{ width: MATRIX_COL_WIDTH.permission }} />)}
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="am-col-module am-sticky-col">Module</th>
                        <th className="am-col-route">Route</th>
                        {allColumns.map((col) => <th key={col} className="am-col-permission">{labelFor(col)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredModules.map((m) => {
                        const isExpanded = expanded.has(m.id) || !!search;
                        const permByKey = new Map(m.permissions.map((p) => [p.key, p]));
                        return (
                          <tr key={m.id}>
                            <td className="am-col-module am-sticky-col">
                              <button type="button" className="am-expand-btn" onClick={() => toggleExpand(m.id)}>
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />} {m.moduleName}
                              </button>
                              {isExpanded && (
                                <div className="am-module-actions">
                                  <Button variant="link" size="sm" onClick={() => selectAllForModule(m)}>
                                    <i className="fa-solid fa-check" aria-hidden="true"></i> Select All
                                  </Button>
                                  <Button variant="link" size="sm" onClick={() => clearAllForModule(m)}>
                                    <i className="fa-solid fa-xmark" aria-hidden="true"></i> Clear All
                                  </Button>
                                </div>
                              )}
                            </td>
                            <td className="am-col-route">{m.routePath ? <code title={m.routePath}>{m.routePath}</code> : <span className="am-muted">—</span>}</td>
                            {allColumns.map((col) => {
                              const p = permByKey.get(col);
                              if (!p) return <td key={col} className="am-disabled-cell am-col-permission" title="Not available for this module">—</td>;
                              const state = readState(pendingChanges, p.id, p);
                              const changed = pendingChanges.has(p.id);
                              const missingScope = state.granted && !state.dataScope;
                              return (
                                <td key={col} className="am-perm-cell am-col-permission">
                                  <div className="am-perm-stack">
                                    <Form.Check
                                      type="checkbox"
                                      checked={state.granted}
                                      onChange={() => toggleGrant(p.id, p)}
                                      className={changed ? "am-changed" : ""}
                                      aria-label={`${m.moduleName} - ${p.name}`}
                                    />
                                    {state.granted && (
                                      <Form.Select
                                        size="sm"
                                        className={`am-scope-select${missingScope ? " am-scope-missing" : ""}`}
                                        value={state.dataScope || ""}
                                        onChange={(e) => setScope(p.id, p, e.target.value)}
                                        aria-label={`${m.moduleName} - ${p.name} - scope`}
                                      >
                                        <option value="">Scope…</option>
                                        {SUPPORTED_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                                      </Form.Select>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)} centered dialogClassName="admin-teal-modal">
        <Modal.Header closeButton><Modal.Title>{confirmAction?.title}</Modal.Title></Modal.Header>
        <Modal.Body>
          {confirmAction?.message} <InfoTip text="This only stages the change locally — nothing is saved until you click Save Changes." />
          {confirmAction?.requiresScope && (
            <Form.Select
              className="am-modal-scope-select"
              value={confirmAction.scope}
              onChange={(e) => setConfirmAction((prev) => ({ ...prev, scope: e.target.value }))}
            >
              <option value="">Select a data scope…</option>
              {SUPPORTED_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Form.Select>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={() => setConfirmAction(null)}>
            <i className="fa-solid fa-xmark" aria-hidden="true"></i> Cancel
          </Button>
          <Button
            className="unit-btn unit-btn-primary"
            disabled={confirmAction?.requiresScope && !confirmAction?.scope}
            onClick={() => { confirmAction?.onConfirm(confirmAction.scope); setConfirmAction(null); }}
          >
            <i className="fa-solid fa-check" aria-hidden="true"></i> Confirm
          </Button>
        </Modal.Footer>
      </Modal>


      <style>{`
        .am-info { color: #94a3b8; cursor: help; margin-left: 6px; }
        .am-unsaved-banner { background: var(--admin-warning-soft, var(--warning-bg, #fffbeb)); color: var(--admin-warning, var(--warning, #d97706)); border-bottom: 1px solid var(--admin-warning, var(--warning, #d97706)); padding: 10px 22px; font-size: 13px; font-weight: 700; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .am-scope-warning { color: #b42318; }
        .am-empty { padding: 40px 20px; text-align: center; color: #667085; font-size: 14px; display: flex; flex-direction: column; align-items: center; gap: 10px; }
        .am-error { color: #b42318; }
        .am-muted { color: #94a3b8; }
        .am-changed { outline: 2px solid #6d1530; border-radius: 4px; }
        .am-scope-missing { border-color: #ef4444 !important; }
        .am-modal-scope-select { margin-top: 12px; }

        /* ---------- Two-column layout: Roles list (left, fixed width) +
           Role details/matrix (right, flex-grow) -- aligned with
           AccessManagementUsersPage.jsx's own layout convention.
           Collapses to a single stacked column on narrower screens. ---------- */
        .arp-layout { display: grid; grid-template-columns: 280px minmax(0, 1fr); align-items: stretch; gap: 20px; padding: 24px; }
        .arp-roles-section, .arp-details-section { width: 100%; min-width: 0; box-sizing: border-box; }
        .arp-roles-section { display: flex; flex-direction: column; height: 100%; position: sticky; top: 96px; }

        .arp-roles-panel-header { padding: 20px 20px 12px; }
        .arp-roles-panel-header h5 { font-size: 16px; font-weight: 700; margin: 0; }
        .arp-roles-panel-header p { font-size: 12.5px; color: #667085; margin: 2px 0 0; }

        .arp-role-mobile-select { display: none; }

        /* flex: 1 (not a fixed viewport calc()) is what actually
           guarantees the roles card matches the permission card's height
           exactly: .arp-layout stretches both grid columns to the same
           row height (align-items: stretch, above), and this list then
           absorbs all of the roles card's remaining space after its own
           header -- so it's always exactly as tall as the taller card,
           on any viewport, regardless of how many roles or permissions
           exist, rather than approximating it with a magic number. */
        .arp-role-card-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 0 12px 12px; display: flex; flex-direction: column; gap: 6px; }
        .arp-role-card { display: flex; align-items: center; gap: 12px; padding: 12px 16px; height: 64px; box-sizing: border-box; border-radius: 14px; border: 1px solid #eef2f6; background: #ffffff; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03); cursor: pointer; transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease; }
        .arp-role-card:hover { background: #FAFBFC; border-color: #d9e1ec; box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06); transform: translateY(-1px); }
        .arp-role-card:focus-visible { outline: 2px solid var(--admin-primary, #7A1733); outline-offset: -2px; }
        /* Spec's dedicated "active" accent is a distinct blue, not the page's
           maroon primary -- --am-blue/--am-blue-soft (defined in
           AccessManagementRolesPage.css) exist specifically for this. The
           left accent line overrides the per-role inline borderLeftColor
           (set in the JSX for the avatar-matched look) so the ACTIVE state
           always reads as the same dedicated blue regardless of role. */
        .arp-role-card-selected { background: var(--am-blue-soft, #EEF5FF); border: 1px solid var(--am-blue, #005BAC); border-left: 3px solid var(--am-blue, #005BAC) !important; box-shadow: 0 4px 12px rgba(0, 91, 172, 0.12); }
        .arp-role-card-avatar { width: 38px; height: 38px; min-width: 38px; border-radius: 999px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; }
        .arp-role-card-text { display: flex; flex-direction: column; min-width: 0; gap: 2px; }
        .arp-role-card-name { font-size: 15px; font-weight: 600; color: #101828; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .arp-role-card-count { font-size: 12px; color: #667085; font-weight: 500; }

        .arp-hero { display: flex; align-items: center; gap: 16px; padding: 20px; min-height: 90px; box-sizing: border-box; border-bottom: 1px solid #eef2f6; }
        .arp-hero-avatar { width: 48px; height: 48px; min-width: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 17px; font-weight: 700; }
        .arp-hero-text h3 { font-size: 22px; font-weight: 700; color: #101828; margin: 0; text-transform: capitalize; }
        .arp-hero-sub { font-size: 14px; color: #667085; margin: 2px 0 0; }

        /* ---------- Permission summary: compact statistic cards (icon /
           value / label, stacked vertically), not horizontal pills ---------- */
        .aup-access-summary { display: flex; flex-wrap: wrap; gap: 12px; padding: 20px; border-bottom: 1px solid #eef2f6; }
        .aup-summary-chip { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; width: 150px; height: 70px; box-sizing: border-box; background: #F8F7F4; border: 1px solid #EAECF0; color: #101828; border-radius: 12px; padding: 8px; }
        .aup-summary-chip i { font-size: 15px; color: #667085; }
        .aup-summary-chip strong { font-size: 20px; font-weight: 700; line-height: 1.1; }
        .aup-summary-chip span { font-size: 11.5px; font-weight: 600; color: #667085; }
        .aup-summary-chip-success { background: #ECFDF3; border-color: rgba(22,163,74,0.25); }
        .aup-summary-chip-success i, .aup-summary-chip-success span { color: #16A34A; }
        .aup-summary-chip-danger { background: #FEF3F2; border-color: rgba(217,45,32,0.25); }
        .aup-summary-chip-danger i, .aup-summary-chip-danger span { color: #D92D20; }

        .arp-matrix-toolbar { padding: 20px; border-bottom: none; }

        .arp-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; min-height: 200px; padding: 26px 30px; color: #667085; }
        .arp-empty-icon { font-size: 32px; color: #cbd5e1; margin-bottom: 10px; }
        .arp-empty-state h5 { font-size: 18px; font-weight: 700; color: #241f1a; margin: 0 0 8px; }
        .arp-empty-state p { font-size: 14px; max-width: 380px; margin: 0; }

        @media (max-width: 1180px) {
          .arp-layout { grid-template-columns: 260px minmax(0, 1fr); }
        }

        /* Tablet: roles panel stays visible but as a shorter, independently-
           scrolling block above the matrix instead of a full-height sticky
           sidebar ("collapsible" space, not a full accordion toggle -- no
           new interactive state was added for this purely visual pass). */
        @media (max-width: 992px) {
          .arp-layout { grid-template-columns: 1fr; padding: 14px 18px; gap: 14px; }
          .arp-roles-section { max-height: 320px; position: static; }
        }

        /* Mobile: the role card list is replaced by a native <select> (same
           selectRole handler as the cards) -- simpler to operate with a
           thumb than a scrolling card list on a phone-width screen. */
        @media (max-width: 640px) {
          .arp-roles-panel-header { display: none; }
          .arp-role-mobile-select { display: block; margin: 12px; width: calc(100% - 24px); height: 44px; border-radius: 10px; }
          .arp-role-card-list { display: none; }
          .arp-roles-section { max-height: none; }
        }

        @media (max-width: 480px) {
          .arp-layout { padding: 12px 14px; gap: 12px; }
          .arp-hero { flex-wrap: wrap; }
        }
      `}</style>
    </div>
  );
}
